import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Combobox,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  toast,
} from '@berky/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  DIREKT,
  usePlatformApi,
  type PlattformNutzer,
  type VerwaltungsPerson,
  type VerzeichnisPerson,
  type VerzeichnisPersonenSeite,
} from './api'
import { RechteVergabeDialog } from './rechte-vergabe-dialog'

/** Wie lange nach dem letzten Tastendruck gewartet wird, bevor gesucht wird. */
const ENTPRELLUNG_MS = 300

/**
 * Personen und ihre Rollen.
 *
 * Die Liste hat zwei Quellen: die Nutzer, die das Portal schon kennt, und das
 * Verzeichnis. Beide werden über die Kennung im Verzeichnis zu je einer Zeile
 * zusammengeführt — sonst stünde jemand, der sich schon angemeldet hat, zweimal
 * da, und man wüsste nicht, welche der beiden Zeilen die Rollen trägt.
 */
export function PersonenTab() {
  const api = usePlatformApi()
  const queryClient = useQueryClient()

  const [eingabe, setEingabe] = useState('')
  const [suche, setSuche] = useState('')
  const [seite, setSeite] = useState(0)
  const [source, setSource] = useState('directory')
  const [fortsetzungen, setFortsetzungen] = useState<string[]>([])

  useEffect(() => {
    const timer = setTimeout(() => {
      setSuche(eingabe)
      setSeite(0)
      setFortsetzungen([])
    }, ENTPRELLUNG_MS)
    return () => clearTimeout(timer)
  }, [eingabe])

  const fortsetzung = seite === 0 ? undefined : fortsetzungen[seite - 1]

  const verzeichnis = useQuery({
    queryKey: ['verwaltung', 'verzeichnis', 'personen', suche, fortsetzung],
    queryFn: ({ signal }) =>
      api.get<VerzeichnisPersonenSeite>('/verzeichnis/personen', {
        query: {
          ...(suche ? { suche } : {}),
          ...(fortsetzung ? { fortsetzung } : {}),
        },
        signal,
      }),
    // Ein nicht erreichbares Verzeichnis ist keine Ausnahmesituation, die man
    // wegretryen sollte — die Seite bleibt mit den lokalen Personen benutzbar.
    retry: false,
    enabled: source === 'directory',
  })

  const useLocal = source === 'local' || verzeichnis.isError
  const localPage = useLocal && source === 'directory' ? 0 : seite
  const displayedPage = useLocal ? localPage : seite
  const verzeichnisPersonen = useLocal ? [] : (verzeichnis.data?.items ?? [])
  const ids = verzeichnisPersonen.map((person) => person.externalId)
  const lokal = useQuery({
    queryKey: ['verwaltung', 'users-page', suche, localPage],
    queryFn: ({ signal }) =>
      api.get<{ items: PlattformNutzer[]; total: number }>('/users/page', {
        query: { suche, page: localPage + 1, pageSize: 25 },
        signal,
      }),
    enabled: useLocal,
  })
  const lookup = useQuery({
    queryKey: ['verwaltung', 'users-lookup', ids],
    queryFn: ({ signal }) =>
      api.post<PlattformNutzer[]>('/users/lookup', ids, { signal }),
    enabled: !useLocal && verzeichnis.isSuccess,
  })
  const personen = zusammenfuehren(
    useLocal ? (lokal.data?.items ?? []) : (lookup.data ?? []),
    verzeichnisPersonen,
  )
  const loadingPeople = useLocal
    ? lokal.isPending
    : verzeichnis.isPending || lookup.isPending
  const peopleError = useLocal ? lokal.isError : lookup.isError
  const hasNextPage = useLocal
    ? (localPage + 1) * 25 < (lokal.data?.total ?? 0)
    : Boolean(verzeichnis.data?.fortsetzung)

  function neuLaden() {
    return queryClient.invalidateQueries({ queryKey: ['verwaltung'] })
  }

  const [ausgewaehltePersonen, setAusgewaehltePersonen] = useState<VerzeichnisPerson[]>(
    [],
  )
  const [vergabeFuer, setVergabeFuer] = useState<VerzeichnisPerson[] | null>(null)
  const [detailPerson, setDetailPerson] = useState<VerwaltungsPerson | null>(null)
  const [rechteFilter, setRechteFilter] = useState<RechteFilter>('alle')
  const [entzugFuer, setEntzugFuer] = useState<{
    person: VerwaltungsPerson
    rolle: string
  } | null>(null)

  const entziehen = useMutation({
    mutationFn: ({ person, rolle }: { person: VerwaltungsPerson; rolle: string }) =>
      api.delete(`/users/${person.id}/roles/${encodeURIComponent(rolle)}`),
    onSuccess: async () => {
      toast.success('Rolle entzogen')
      setEntzugFuer(null)
      await neuLaden()
    },
  })

  const gefiltertePersonen = personen.filter((person) => {
    if (rechteFilter === 'mit-rechten') return person.roles.length > 0
    if (rechteFilter === 'ohne-rechte') return person.roles.length === 0
    if (rechteFilter === 'direkt') {
      return person.roles.some((eintrag) => eintrag.source === DIREKT)
    }
    if (rechteFilter === 'gruppe') {
      return person.roles.some((eintrag) => eintrag.source !== DIREKT)
    }
    return true
  })

  return (
    <div className="space-y-4 pt-4">
      {verzeichnis.isError && (
        <Alert>
          <TriangleAlert aria-hidden="true" />
          <AlertTitle>Verzeichnis nicht erreichbar</AlertTitle>
          <AlertDescription>
            Das Verzeichnis ist gerade nicht erreichbar. Angezeigt werden nur Personen,
            die sich bereits angemeldet haben.
          </AlertDescription>
        </Alert>
      )}

      <Select
        value={source}
        onValueChange={(value) => {
          setSource(value)
          setSeite(0)
          setFortsetzungen([])
          setAusgewaehltePersonen([])
        }}
      >
        <SelectTrigger aria-label="Personenquelle" className="w-full sm:w-72">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="directory">Personen aus Microsoft 365</SelectItem>
          <SelectItem value="local">Lokal bekannte Plattformkonten</SelectItem>
        </SelectContent>
      </Select>
      <Combobox<VerzeichnisPerson>
        items={verzeichnisPersonen}
        value={null}
        onChange={(person) => setEingabe(person.name)}
        onSearch={setEingabe}
        getKey={(person) => person.externalId}
        getLabel={(person) => person.name}
        getDescription={(person) => person.email}
        placeholder="Person suchen"
        emptyText="Niemand gefunden"
        loading={verzeichnis.isFetching}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
        <div>
          <p className="font-medium">Rechte gesammelt vergeben</p>
          <p className="text-sm text-muted-foreground">
            Wählen Sie mehrere Personen aus, wenn dieselbe Rolle für alle gelten soll.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={ausgewaehltePersonen.length === 0}
          onClick={() => setVergabeFuer(ausgewaehltePersonen)}
        >
          {ausgewaehltePersonen.length > 0
            ? `Rolle für ${ausgewaehltePersonen.length} Personen vergeben`
            : 'Personen auswählen'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {gefiltertePersonen.length} von {personen.length} Personen angezeigt
        </p>
        <Select
          value={rechteFilter}
          onValueChange={(value) => setRechteFilter(value as RechteFilter)}
        >
          <SelectTrigger aria-label="Personen nach Rechten filtern" className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Personen</SelectItem>
            <SelectItem value="mit-rechten">Mit Rechten</SelectItem>
            <SelectItem value="ohne-rechte">Ohne Rechte</SelectItem>
            <SelectItem value="direkt">Mit direkter Zuweisung</SelectItem>
            <SelectItem value="gruppe">Mit Rechten über Gruppe</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable<VerwaltungsPerson>
        caption="Personen und ihre Rollen"
        rows={gefiltertePersonen}
        rowKey={(person) => person.externalId}
        isLoading={loadingPeople}
        error={
          peopleError
            ? {
                onRetry: () => {
                  void lokal.refetch()
                  void lookup.refetch()
                },
              }
            : null
        }
        emptyState={
          <EmptyState
            title="Keine Person gefunden"
            description="Suchen Sie nach Namen, E-Mail-Adressen oder Anmeldenamen."
          />
        }
        columns={[
          {
            key: 'auswahl',
            header: 'Auswahl',
            className: 'w-16',
            cell: (person) => (
              <Checkbox
                checked={ausgewaehltePersonen.some(
                  (auswahl) => auswahl.externalId === person.externalId,
                )}
                aria-label={`${person.name} auswählen`}
                onCheckedChange={(checked) =>
                  setAusgewaehltePersonen((aktuell) =>
                    checked
                      ? [...aktuell, alsVerzeichnisPerson(person)]
                      : aktuell.filter(
                          (auswahl) => auswahl.externalId !== person.externalId,
                        ),
                  )
                }
              />
            ),
          },
          {
            key: 'name',
            header: 'Person',
            cell: (person) => (
              <div className="flex flex-col gap-1">
                <span className="font-medium">{person.name}</span>
                <span className="text-xs text-muted-foreground">{person.email}</span>
                {person.id === null && (
                  <StatusBadge tone="neutral" className="w-fit">
                    noch nie angemeldet
                  </StatusBadge>
                )}
                {person.nurLokal && (
                  <StatusBadge tone="warning" className="w-fit">
                    M365-Status ungeprüft
                  </StatusBadge>
                )}
              </div>
            ),
          },
          {
            key: 'rollen',
            header: 'Wirksame Rechte',
            cell: (person) =>
              person.roles.length === 0 ? (
                <span className="text-sm text-muted-foreground">Keine</span>
              ) : (
                <ul className="space-y-1">
                  {person.roles.map((eintrag) => (
                    <li
                      key={`${eintrag.role}:${eintrag.source}`}
                      className="flex flex-wrap items-center gap-2"
                    >
                      <StatusBadge tone={eintrag.source === DIREKT ? 'info' : 'neutral'}>
                        {eintrag.role}
                      </StatusBadge>
                      <StatusBadge tone={eintrag.source === DIREKT ? 'info' : 'neutral'}>
                        {eintrag.source === DIREKT ? 'direkt' : `über ${eintrag.source}`}
                      </StatusBadge>
                      {eintrag.source === DIREKT ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEntzugFuer({ person, rolle: eintrag.role })}
                          aria-label={`${eintrag.role} entziehen`}
                        >
                          Entziehen
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {`Diese Rolle kommt aus der Gruppe ${eintrag.source}. Nehmen Sie sie dort weg.`}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ),
          },
          {
            key: 'aktionen',
            header: 'Aktion',
            align: 'right',
            cell: (person) => (
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setDetailPerson(person)}>
                  Rechte ansehen
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setVergabeFuer([alsVerzeichnisPerson(person)])}
                  aria-label={`Rolle vergeben an ${person.name}`}
                >
                  Rolle vergeben
                </Button>
              </div>
            ),
          },
        ]}
      />

      {(displayedPage > 0 || hasNextPage) && (
        <div className="flex items-center justify-end gap-3">
          <span className="text-sm text-muted-foreground">{`Seite ${displayedPage + 1}`}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={displayedPage === 0 || loadingPeople}
            onClick={() => setSeite((aktuell) => Math.max(0, aktuell - 1))}
          >
            Zurück
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNextPage || loadingPeople}
            onClick={() => {
              if (useLocal) {
                if (source === 'directory') setSource('local')
                setSeite(localPage + 1)
                return
              }
              const naechsteFortsetzung = verzeichnis.data?.fortsetzung
              if (!naechsteFortsetzung) return

              setFortsetzungen((aktuell) => [
                ...aktuell.slice(0, seite),
                naechsteFortsetzung,
              ])
              setSeite((aktuell) => aktuell + 1)
            }}
          >
            Weiter
          </Button>
        </div>
      )}

      {vergabeFuer !== null && (
        <RechteVergabeDialog
          initialPersons={vergabeFuer}
          open
          onClose={() => setVergabeFuer(null)}
          onDone={neuLaden}
        />
      )}

      <Dialog
        open={detailPerson !== null}
        onOpenChange={(offen) => !offen && setDetailPerson(null)}
      >
        {detailPerson && (
          <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Wirksame Rechte: {detailPerson.name}</DialogTitle>
              <DialogDescription>
                Diese Übersicht zeigt direkte Zuweisungen und Rechte, die aus einer Gruppe
                kommen.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                <p className="font-medium">{detailPerson.email}</p>
                <p className="text-muted-foreground">
                  {detailPerson.roles.length === 0
                    ? 'Derzeit sind keine Rollen zugewiesen.'
                    : `${detailPerson.roles.length} wirksame Rollen`}
                </p>
              </div>
              {detailPerson.roles.length > 0 && (
                <ul className="space-y-3">
                  {detailPerson.roles.map((eintrag) => (
                    <li
                      key={`${eintrag.role}:${eintrag.source}`}
                      className="rounded-lg border p-3"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge tone="info">{eintrag.role}</StatusBadge>
                        <StatusBadge
                          tone={eintrag.source === DIREKT ? 'info' : 'neutral'}
                        >
                          {eintrag.source === DIREKT
                            ? 'direkt vergeben'
                            : `über ${eintrag.source}`}
                        </StatusBadge>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {eintrag.source === DIREKT
                          ? 'Diese Rolle wurde dieser Person direkt zugewiesen.'
                          : `Die Gruppe ${eintrag.source} vererbt diese Rolle. Entfernen Sie die Rolle dort, wenn sie für alle Gruppenmitglieder entfallen soll.`}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailPerson(null)}>
                Schließen
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      <ConfirmDialog
        open={entzugFuer !== null}
        onOpenChange={(offen) => !offen && setEntzugFuer(null)}
        title="Rolle entziehen?"
        description={
          entzugFuer
            ? `${entzugFuer.person.name} verliert damit die Rolle ${entzugFuer.rolle}.`
            : ''
        }
        confirmLabel="Entziehen"
        destructive
        pending={entziehen.isPending}
        onConfirm={() => entzugFuer && entziehen.mutate(entzugFuer)}
      />
    </div>
  )
}

type RechteFilter = 'alle' | 'mit-rechten' | 'ohne-rechte' | 'direkt' | 'gruppe'

function alsVerzeichnisPerson(person: VerwaltungsPerson): VerzeichnisPerson {
  return {
    externalId: person.externalId,
    name: person.name,
    email: person.email,
  }
}

/**
 * Führt lokale Nutzer und Verzeichnistreffer über die Kennung im Verzeichnis zu
 * je einer Zeile zusammen.
 *
 * Absence from a directory page does not prove deletion. `nurLokal` means
 * that membership in Microsoft 365 has not been verified for this row.
 */
export function zusammenfuehren(
  lokal: PlattformNutzer[],
  verzeichnis: VerzeichnisPerson[],
): VerwaltungsPerson[] {
  const ausVerzeichnis = new Set(verzeichnis.map((person) => person.externalId))
  const bekannt = new Map(lokal.map((nutzer) => [nutzer.externalId, nutzer]))

  const zeilen: VerwaltungsPerson[] = lokal.map((nutzer) => ({
    id: nutzer.id,
    externalId: nutzer.externalId,
    name: nutzer.name,
    email: nutzer.email,
    roles: nutzer.roles,
    nurLokal: !ausVerzeichnis.has(nutzer.externalId),
  }))

  for (const person of verzeichnis) {
    if (bekannt.has(person.externalId)) continue

    zeilen.push({
      id: null,
      externalId: person.externalId,
      name: person.name,
      email: person.email,
      roles: [],
      nurLokal: false,
    })
  }

  return zeilen.sort((a, b) => a.name.localeCompare(b.name, 'de'))
}
